import { z } from 'zod';

/** Scope kinds stored on overrides. `step` and `global` reserved for future phases. */
export const TEST_DATA_OVERRIDE_SCOPE_KINDS = ['flow', 'scenario', 'step', 'global'] as const;
export type TestDataOverrideScopeKind = (typeof TEST_DATA_OVERRIDE_SCOPE_KINDS)[number];

/** Active resolution scopes (highest wins first). */
export const TEST_DATA_RESOLUTION_SCOPES = ['step', 'scenario', 'flow', 'project', 'org', 'global'] as const;
export type TestDataResolutionScope = (typeof TEST_DATA_RESOLUTION_SCOPES)[number];

/** Full target hierarchy. */
export const TEST_DATA_SCOPE_HIERARCHY = ['step', 'scenario', 'flow', 'project', 'org', 'global'] as const;
export type TestDataScopeHierarchy = (typeof TEST_DATA_SCOPE_HIERARCHY)[number];

export const TEST_DATA_RESOLUTION_STATUSES = [
  'resolved',
  'project_default',
  'no_override',
  'disabled_override',
  'missing_catalogue_key',
  'invalid_row_index',
  'conflicting_override',
  'catalogue_archived',
  'override_unsupported_type',
  'template_not_adopted',
  'conflicting_template',
  'missing_template',
] as const;
export type TestDataResolutionStatus = (typeof TEST_DATA_RESOLUTION_STATUSES)[number];

export const TestDataOverrideSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  catalogueItemId: z.string().uuid(),
  catalogueKey: z.string(),
  scopeKind: z.enum(TEST_DATA_OVERRIDE_SCOPE_KINDS),
  scopeId: z.string().uuid(),
  scopeName: z.string().nullable().optional(),
  enabled: z.boolean(),
  rowIndex: z.number().int().nonnegative().nullable(),
  selectedRowIndexes: z.array(z.number().int().nonnegative()).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByName: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
});
export type TestDataOverride = z.infer<typeof TestDataOverrideSchema>;

export const TestDataOverrideListResponseSchema = z.object({
  projectId: z.string().uuid(),
  items: z.array(TestDataOverrideSchema),
});
export type TestDataOverrideListResponse = z.infer<typeof TestDataOverrideListResponseSchema>;

export const CreateTestDataOverrideRequestSchema = z.object({
  catalogueItemId: z.string().uuid(),
  scopeKind: z.enum(['flow', 'scenario', 'step']),
  scopeId: z.string().uuid(),
  enabled: z.boolean().optional(),
  rowIndex: z.number().int().nonnegative().nullable().optional(),
  selectedRowIndexes: z.array(z.number().int().nonnegative()).max(100).optional(),
});
export type CreateTestDataOverrideRequest = z.infer<typeof CreateTestDataOverrideRequestSchema>;

export const PatchTestDataOverrideRequestSchema = z.object({
  enabled: z.boolean().optional(),
  rowIndex: z.number().int().nonnegative().nullable().optional(),
  selectedRowIndexes: z.array(z.number().int().nonnegative()).max(100).optional(),
});
export type PatchTestDataOverrideRequest = z.infer<typeof PatchTestDataOverrideRequestSchema>;

export const TestDataOverrideResponseSchema = z.object({
  item: TestDataOverrideSchema,
});
export type TestDataOverrideResponse = z.infer<typeof TestDataOverrideResponseSchema>;

export const ResolvedTestDataEntrySchema = z.object({
  catalogueKey: z.string(),
  catalogueItemId: z.string().uuid(),
  provenance: z.enum(TEST_DATA_RESOLUTION_SCOPES),
  effectiveScope: z.enum(TEST_DATA_RESOLUTION_SCOPES),
  status: z.enum(TEST_DATA_RESOLUTION_STATUSES),
  overrideId: z.string().uuid().nullable().optional(),
  selectedRowIndex: z.number().int().nonnegative().nullable().optional(),
  fallbackReason: z.string().max(500).optional(),
  rowPreview: z.record(z.string(), z.string()).optional(),
  rowCount: z.number().int().nonnegative().optional(),
});
export type ResolvedTestDataEntry = z.infer<typeof ResolvedTestDataEntrySchema>;

export const TestDataResolveRequestSchema = z.object({
  flowId: z.string().uuid().optional(),
  scenarioId: z.string().uuid().optional(),
  stepId: z.string().uuid().optional(),
  catalogueKey: z.string().trim().min(1).max(80).optional(),
});
export type TestDataResolveRequest = z.infer<typeof TestDataResolveRequestSchema>;

export const TestDataResolveResponseSchema = z.object({
  projectId: z.string().uuid(),
  flowId: z.string().uuid().nullable().optional(),
  scenarioId: z.string().uuid().nullable().optional(),
  stepId: z.string().uuid().nullable().optional(),
  entries: z.array(ResolvedTestDataEntrySchema),
  data: z.record(z.string(), z.unknown()),
  datasets: z.record(z.string(), z.array(z.record(z.string(), z.string()))).default({}),
  environment: z.record(z.string(), z.string()).default({}),
  errors: z.array(
    z.object({
      catalogueKey: z.string(),
      status: z.enum(TEST_DATA_RESOLUTION_STATUSES),
      message: z.string(),
    }),
  ),
});
export type TestDataResolveResponse = z.infer<typeof TestDataResolveResponseSchema>;
